import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import defaultMetaConfig from '../utils/metaConfig';

// Create context
const MetaContext = createContext();

/**
 * MetaProvider component
 * Provides meta tag management functionality to the app
 */
export const MetaProvider = ({ children }) => {
  const location = useLocation();
  const [meta, setMeta] = useState(defaultMetaConfig.defaults);
  
  // Update meta when route changes using defaults
  useEffect(() => {
    // Get the path without leading slash
    const path = location.pathname.replace(/^\//, '');
    const pathKey = path || 'home';
    
    // Find matching page config based on path
    // First try exact match, then fallback to path segment
    const pageMatch = Object.keys(defaultMetaConfig.pages).find(
      (key) => key === pathKey
    );
    
    // If no exact match, check if the path starts with a known key
    const segmentMatch = !pageMatch 
      ? Object.keys(defaultMetaConfig.pages).find(
          (key) => pathKey.startsWith(`${key}/`) || pathKey === key
        )
      : null;
    
    const matchedKey = pageMatch || segmentMatch || 'home';
    
    console.log(`[MetaContext] Path: "${path}", Matched key: "${matchedKey}"`, {
      pathKey,
      pageMatch,
      segmentMatch,
      availableKeys: Object.keys(defaultMetaConfig.pages)
    });
    
    // Merge defaults with page-specific config
    setMeta({
      ...defaultMetaConfig.defaults,
      ...(defaultMetaConfig.pages[matchedKey] || {}),
      // Always ensure URL is current
      url: window.location.href,
    });
  }, [location.pathname]);
  
  // Update meta tags with new values
  const updateMeta = (newMetaData) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      ...newMetaData,
    }));
  };
  
  // Reset meta tags to defaults
  const resetMeta = () => {
    setMeta(defaultMetaConfig.defaults);
  };
  
  // Generate the full title with site name
  const getFullTitle = () => {
    return meta.title.includes(defaultMetaConfig.defaults.siteName)
      ? meta.title
      : `${meta.title} | ${defaultMetaConfig.defaults.siteName}`;
  };

  return (
    <MetaContext.Provider value={{ meta, updateMeta, resetMeta }}>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{getFullTitle()}</title>
        <meta name="description" content={meta.description} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content={meta.type} />
        <meta property="og:url" content={meta.url} />
        <meta property="og:title" content={getFullTitle()} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:image" content={meta.image} />
        <meta property="og:locale" content={meta.locale} />
        <meta property="og:site_name" content={defaultMetaConfig.defaults.siteName} />
        
        {/* Twitter */}
        <meta name="twitter:card" content={meta.twitterCard} />
        <meta name="twitter:url" content={meta.url} />
        <meta name="twitter:title" content={getFullTitle()} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />
      </Helmet>
      {children}
    </MetaContext.Provider>
  );
};

// Export context
export default MetaContext;